/**
 * BIP-39 24-word mnemonic <-> 32-byte family key.
 *
 * Mirrors the Rust `backstop_crypto::mnemonic` API. We use the mnemonic purely
 * as a lossless serialization of the raw 32 bytes: no PBKDF2 stretching (which
 * BIP-39 defines for wallet seeds). This must produce the same word list for
 * the same entropy on both sides.
 */

import { entropyToMnemonic, mnemonicToEntropy } from '@scure/bip39';
import { wordlist } from '@scure/bip39/wordlists/english.js';

/** Produce a 24-word BIP-39 English mnemonic from a 32-byte family key. */
export function mnemonicFromFamilyKey(familyKey: Uint8Array): string {
  if (familyKey.length !== 32) {
    throw new Error(
      `family key must be 32 bytes, got ${familyKey.length.toString()}`,
    );
  }
  return entropyToMnemonic(familyKey, wordlist);
}

/** Recover the 32-byte family key from a 24-word BIP-39 English mnemonic. */
export function familyKeyFromMnemonic(mnemonic: string): Uint8Array {
  const entropy = mnemonicToEntropy(mnemonic.trim(), wordlist);
  if (entropy.length !== 32) {
    throw new Error(
      `expected 32 bytes of entropy (24 words), got ${entropy.length.toString()}`,
    );
  }
  return entropy;
}
